// frontend/src/utils/logger.js
// Purpose: Consistent logging across frontend application

// Desired outcome: Provide a standardized logging system with different 
// severity levels to help with debugging and monitoring application behavior

// Define log levels with corresponding colors
const LOG_LEVELS = {
    ERROR: { value: 0, color: '#FF0000', label: 'ERROR' },  // Red
    WARN: { value: 1, color: '#FFA500', label: 'WARN' },   // Orange
    INFO: { value: 2, color: '#00FF00', label: 'INFO' },   // Green
    DEBUG: { value: 3, color: '#0000FF', label: 'DEBUG' }  // Blue
  };
  
  // Get current log level from environment or default to INFO
  const getCurrentLogLevel = () => {
    const envLevel = process.env.REACT_APP_LOG_LEVEL || 'INFO';
    return LOG_LEVELS[envLevel] ? LOG_LEVELS[envLevel].value : LOG_LEVELS.INFO.value;
  };
  
  // Current log level (can be overridden)
  let currentLogLevel = getCurrentLogLevel();
  
  // Helper to format date consistently
  const formatDate = () => {
    const now = new Date();
    return now.toISOString();
  };
  
  // Helper to format messages
  const formatMessage = (level, message, data) => {
    const timestamp = formatDate();
    const prefix = `[${timestamp}] [${level.label}]:`;
    
    // Basic console styling
    const style = `color: ${level.color}; font-weight: bold;`;
    
    if (data) {
      return { prefix, message, data, style };
    }
    
    return { prefix, message, style };
  };
  
  // Log method implementation
  const log = (level, message, data) => {
    // Only log if the level is less than or equal to current log level
    if (level.value <= currentLogLevel) {
      const { prefix, message: formattedMessage, data: logData, style } = formatMessage(level, message, data);
      
      if (data) {
        console.log(`%c${prefix} ${formattedMessage}`, style, logData);
      } else {
        console.log(`%c${prefix} ${formattedMessage}`, style);
      }
      
      // For errors, we might want to send to a monitoring service
      if (level === LOG_LEVELS.ERROR && process.env.NODE_ENV === 'production') {
        // Implementation for sending logs to monitoring service would go here
        // e.g., Sentry, LogRocket, etc.
      }
    }
  };
  
  // Public API
  const logger = {
    // Set the log level
    setLogLevel: (level) => {
      if (LOG_LEVELS[level]) {
        currentLogLevel = LOG_LEVELS[level].value;
        logger.info(`Log level set to ${level}`);
      } else {
        logger.warn(`Invalid log level: ${level}. Using default.`);
      }
    },
    
    // Error level logging
    error: (message, data) => {
      log(LOG_LEVELS.ERROR, message, data);
    },
    
    // Warning level logging
    warn: (message, data) => {
      log(LOG_LEVELS.WARN, message, data);
    },
    
    // Info level logging
    info: (message, data) => {
      log(LOG_LEVELS.INFO, message, data);
    },
    
    // Debug level logging
    debug: (message, data) => {
      log(LOG_LEVELS.DEBUG, message, data);
    },
    
    // Log a group of related messages (collapsed in console)
    group: (label, logFunc) => {
      if (typeof console.groupCollapsed === 'function') {
        console.groupCollapsed(label);
        try {
          logFunc();
        } finally {
          console.groupEnd();
        }
      } else {
        logger.info(`===== ${label} =====`);
        logFunc();
        logger.info(`===== END ${label} =====`);
      }
    },
    
    // Log performance timings
    time: (label) => {
      if (typeof console.time === 'function') {
        console.time(label);
      }
    },
    
    // End performance timing
    timeEnd: (label) => {
      if (typeof console.timeEnd === 'function') {
        console.timeEnd(label);
      }
    },
    
    // Get the current log level
    getLogLevel: () => {
      const levelEntry = Object.entries(LOG_LEVELS).find(
        ([_, obj]) => obj.value === currentLogLevel
      );
      return levelEntry ? levelEntry[0] : 'UNKNOWN';
    }
  };
  
  export default logger;